/* eslint-disable max-len */
import React, {
    ChangeEvent,
    KeyboardEvent,
    ReactElement,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import { isEqual } from 'lodash/fp';
import { Tag } from 'antd';
import { validEmailRegex } from 'features/authentication/form-validations/form-validations';
import { CloseIcon } from 'components/assets/Icons/CloseIcon';
import styles from './email-tags.module.scss';

const tagAnimationOutTime = 200;
const defaultMaxEmails = 50;
const extraInputPadding = 5;
const placeholderText = 'name@example.com';

type TagState = {
    tagContent: string;
    isValid: boolean;
};

type AddEmailTagsProps = {
    onEmailsChanged: (emailList: string[]) => void;
    maxEmails?: number;
    className?: string;
};

export const EmailTags = ({
    onEmailsChanged,
    maxEmails = defaultMaxEmails,
    className,
}: AddEmailTagsProps) => {
    const [tags, setTags] = useState<TagState[]>([]);
    const [tagsToRemove, setTagsToRemove] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [inputWidth, setInputWidth] = useState<number>(2);
    const lastEmailsSent = useRef<string[]>([]);
    const spanRef = useRef<HTMLElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const isEmailTagUnique = () => inputValue && !tags.find((tag) => tag.tagContent === inputValue);

    const addInputToTags = () => {
        if (isEmailTagUnique()) {
            const isValid = validEmailRegex.test(inputValue);
            setInputValue('');
            setTags([
                ...tags,
                {
                    tagContent: inputValue,
                    isValid,
                },
            ]);
        }
    };

    const removeTag = (tagToRemove: string) => {
        setTagsToRemove([...tagsToRemove, tagToRemove]);
        const updatedTags = tags.filter((tag) => tag.tagContent !== tagToRemove);
        setTimeout(() => {
            setTags(updatedTags);
            setTagsToRemove([]);
            triggerInputFieldFocus();
        }, tagAnimationOutTime);
    };

    useEffect(() => {
        const validTags = tags.filter((tag) => tag.isValid).map<string>((tag) => tag.tagContent);
        if (!isEqual(lastEmailsSent.current, validTags)) {
            lastEmailsSent.current = validTags;
            onEmailsChanged(validTags);
        }
    }, [onEmailsChanged, tags]);

    useEffect(() => {
        const outsideClickListener = (e: Event) => {
            if (inputContainerRef.current) {
                const target = e.target as HTMLInputElement;
                if (!inputContainerRef.current.contains(target)) {
                    //if click event's target is not another input field, focus on this component's input field
                    if (target.type !== 'textarea' && target.type !== 'input') {
                        triggerInputFieldFocus();
                    }
                }
            }
        };
        document.addEventListener('click', outsideClickListener);
        return () => document.removeEventListener('click', outsideClickListener);
    }, []);

    useLayoutEffect(() => {
        //use invisible span to determine input's width
        const rect = spanRef.current?.getBoundingClientRect();
        setInputWidth((rect?.width || 0) + extraInputPadding);
    }, [inputValue]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };
    const onBlur = () => {
        addInputToTags();
    };
    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            addInputToTags();
        }
    };
    const triggerInputFieldFocus = () => {
        inputRef.current && inputRef.current.focus();
    };

    const tagElements = tags.map<ReactElement>((tag) => {
        const isBeingRemoved = tagsToRemove.includes(tag.tagContent);
        return (
            <div key={tag.tagContent} className={styles.singleTagWrapper}>
                <Tag
                    closable
                    closeIcon={
                        <div className={styles.closeIconContainer}>
                            <CloseIcon />
                        </div>
                    }
                    className={classNames(styles.tag, {
                        [styles.removeTagAnimation]: isBeingRemoved,
                    })}
                    onClose={(e) => {
                        e.preventDefault();
                        removeTag(tag.tagContent);
                    }}
                >
                    {tag.tagContent}
                    {!tag.isValid && <span className={styles.errorMessage}>Invalid Email</span>}
                </Tag>
            </div>
        );
    });

    const getPlaceholderText = () => (!inputValue.length && !tags.length ? placeholderText : '');

    return (
        <div className={classNames(styles.mainContainer, className)}>
            <div className={styles.tagsContainer}>
                {tagElements}
                {tags.length < maxEmails && (
                    <div
                        className={styles.inputContainer}
                        ref={inputContainerRef}
                        onClick={triggerInputFieldFocus}
                    >
                        <span className={styles.placeholder}>{getPlaceholderText()}</span>
                        <input
                            ref={inputRef}
                            className={styles.inputField}
                            value={inputValue}
                            onChange={handleInputChange}
                            onBlur={onBlur}
                            onKeyDown={onKeyDown}
                            style={{ width: `${inputWidth}px` }}
                            type="text"
                            spellCheck="false"
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            aria-autocomplete="both"
                            aria-label={'Add email address'}
                            autoFocus
                        />
                        <span ref={spanRef} className={styles.invisibleSpan}>
                            {inputValue}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
